/**
 * Riva — Build Your Own Trio
 *
 * Maps three chosen products onto a single bundle-product variant and adds that
 * one variant to the cart. The trio price lives on the variant, so this script
 * is not the thing enforcing the price — Shopify is. If it were disabled, the
 * customer simply could not build a trio.
 *
 * The lookup key is the three product handles, sorted and joined with "|",
 * matched against the riva.combo metafield rendered into the page by the
 * section. Sorting is what makes pick order irrelevant.
 *
 * A combination cannot contain the same product twice, so choosing a product
 * that is already in a slot is refused rather than silently ignored.
 */
(function () {
  'use strict';

  var ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth'];

  function initRoot(root) {
    if (root.dataset.byoReady === 'true') return;
    root.dataset.byoReady = 'true';

    var slotCount = parseInt(root.dataset.slots, 10) || 3;
    var moneyFormat = root.dataset.moneyFormat || '${{amount}}';

    // combo key -> { id, price, available }. Empty if the section has no bundle
    // product set, in which case nothing can be added and we say so.
    var combos = {};
    var combosEl = root.querySelector('[data-byo-combos]');
    if (combosEl) {
      try {
        combos = JSON.parse(combosEl.textContent) || {};
      } catch (e) {
        combos = {};
        console.error('[riva-byo] combo map is not valid JSON', e);
      }
    }

    // An empty map means the section is misconfigured — no bundle product set,
    // or it is not published to the Online Store, so Liquid cannot see it. That
    // is a very different problem from one unavailable combination, and saying
    // so here saves guessing at it from the storefront.
    var configured = Object.keys(combos).length > 0;
    if (!configured) {
      console.error(
        '[riva-byo] no trio variants found. Check that the bundle product is set ' +
        'on this section, is published to the Online Store, and that its variants ' +
        'have a riva.combo metafield.'
      );
    }

    /** The trio the current selection resolves to, or null. */
    function matchedCombo() {
      if (chosen.length !== slotCount) return null;
      var key = chosen
        .map(function (c) { return c.handle; })
        .sort()
        .join('|');
      return combos[key] || null;
    }

    var slotEls = Array.prototype.slice.call(root.querySelectorAll('[data-byo-slot]'));
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-byo-product]'));
    var bar = root.querySelector('[data-byo-bar]');
    var countEl = root.querySelector('[data-byo-count]');
    var subtotalEl = root.querySelector('[data-byo-subtotal]');
    var savingsEl = root.querySelector('[data-byo-savings]');
    var totalEl = root.querySelector('[data-byo-total]');
    var submit = root.querySelector('[data-byo-submit]');
    var submitLabel = root.querySelector('[data-byo-submit-label]');
    var errorEl = root.querySelector('[data-byo-error]');

    /** @type {{id:string,price:number,title:string,card:HTMLElement}[]} */
    var chosen = [];

    // Mirrors Shopify's money_format so the estimate matches the cart's own
    // formatting rather than inventing one.
    function money(cents) {
      var value = (cents / 100).toFixed(2);
      var parts = value.split('.');
      var withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      var amount = withCommas + '.' + parts[1];
      var out = moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/g, amount);
      // If the format string had no placeholder we'd be left with raw markup.
      return out.indexOf(amount) === -1 ? '$' + amount : out;
    }

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
    }

    function clearError() {
      if (!errorEl) return;
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    function render() {
      var n = chosen.length;

      slotEls.forEach(function (slot, i) {
        var pick = chosen[i];
        var label = slot.querySelector('.riva-byo__slotlabel');
        var remove = slot.querySelector('[data-byo-remove]');
        if (pick) {
          slot.classList.add('is-filled');
          if (label) label.textContent = pick.title;
          if (remove) remove.hidden = false;
        } else {
          slot.classList.remove('is-filled');
          if (label) label.textContent = 'Pick your ' + (ORDINALS[i] || 'next') + ' product';
          if (remove) remove.hidden = true;
        }
      });

      // Badge each card with how many times it has been chosen.
      cards.forEach(function (card) {
        var id = card.dataset.variantId;
        var times = chosen.filter(function (c) { return c.id === id; }).length;
        var qty = card.querySelector('[data-byo-qty]');
        card.classList.toggle('is-selected', times > 0);
        if (qty) {
          if (times > 1) {
            qty.hidden = false;
            qty.textContent = '×' + times;
          } else {
            qty.hidden = true;
          }
        }
      });

      if (bar) bar.style.width = Math.round((n / slotCount) * 100) + '%';
      if (countEl) countEl.textContent = String(n);

      var subtotal = chosen.reduce(function (sum, c) { return sum + c.price; }, 0);
      var complete = n === slotCount;
      var combo = matchedCombo();

      // The trio price is read off the bundle variant, never calculated here, so
      // the figure on screen is exactly what Shopify will charge. Both are in cents.
      var trioPrice = combo ? combo.price : null;
      var savings = trioPrice === null ? 0 : subtotal - trioPrice;

      if (subtotalEl) subtotalEl.innerHTML = n ? money(subtotal) : '&mdash;';
      if (savingsEl) savingsEl.innerHTML = trioPrice === null ? '&mdash;' : '−' + money(savings);
      if (totalEl) {
        totalEl.innerHTML = trioPrice === null
          ? (n ? money(subtotal) : '&mdash;')
          : money(trioPrice);
      }

      var buyable = complete && !!combo && combo.available !== false;
      if (submit) submit.disabled = !buyable;
      if (submitLabel) {
        if (buyable) {
          submitLabel.textContent = 'Add trio to cart';
        } else if (complete && combo) {
          submitLabel.textContent = 'Sold out';
        } else if (complete && !configured) {
          submitLabel.textContent = 'Trio builder unavailable';
        } else if (complete) {
          submitLabel.textContent = 'That combination is unavailable';
        } else {
          var left = slotCount - n;
          submitLabel.textContent = 'Pick ' + left + ' more product' + (left === 1 ? '' : 's');
        }
      }
    }

    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        if (card.disabled) return;
        clearError();
        if (chosen.length >= slotCount) {
          showError('That is ' + slotCount + ' already — remove one to swap it out.');
          return;
        }
        var already = chosen.some(function (c) { return c.handle === card.dataset.handle; });
        if (already) {
          showError('That one is already in your trio — pick three different formulas.');
          return;
        }
        chosen.push({
          id: card.dataset.variantId,
          handle: card.dataset.handle || '',
          price: parseInt(card.dataset.price, 10) || 0,
          title: card.dataset.title || '',
          card: card
        });
        render();
      });
    });

    slotEls.forEach(function (slot, i) {
      var remove = slot.querySelector('[data-byo-remove]');
      if (!remove) return;
      remove.addEventListener('click', function (e) {
        e.stopPropagation();
        if (!chosen[i]) return;
        chosen.splice(i, 1);
        clearError();
        render();
      });
    });

    if (submit) {
      submit.addEventListener('click', function () {
        if (chosen.length !== slotCount) return;
        clearError();
        submit.disabled = true;
        if (submitLabel) submitLabel.textContent = 'Adding…';

        var combo = matchedCombo();
        if (!combo) {
          showError(configured
            ? 'That combination is not available right now. Try swapping one out.'
            : 'The trio builder is temporarily unavailable. Please try again shortly.');
          submit.disabled = false;
          if (submitLabel) submitLabel.textContent = 'Add trio to cart';
          return;
        }

        // One line: the bundle variant. Shopify expands it into its three
        // components on the order for fulfilment.
        var items = [{ id: combo.id, quantity: 1 }];

        fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
                ? window.Shopify.routes.root + 'cart/add.js'
                : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        })
          .then(function (res) {
            if (!res.ok) return res.json().then(function (d) { throw new Error(d.description || d.message || 'Could not add to cart.'); });
            return res.json();
          })
          .then(function () {
            window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
              ? window.Shopify.routes.root : '/') + 'cart';
          })
          .catch(function (err) {
            showError(err.message || 'Something went wrong adding your trio. Please try again.');
            submit.disabled = false;
            if (submitLabel) submitLabel.textContent = 'Add trio to cart';
          });
      });
    }

    render();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-byo-root]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Re-init when the section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
